"use client";

import React, { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-2">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function WhatsAppFloatingButton() {
  const phone = "221781666638";
  const businessName = "KBC Coffee Service";
  const message = `Bonjour ${businessName},

Je souhaite des informations sur vos produits.

Localisation : (Thiès / Dakar / Petite Côte)
Besoin : Capsules / Machine / Entreprise

Merci.`;
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return <Link to={url} newTab className="fixed bottom-6 right-6 z-50 group">
      <div className="bg-green-600 hover:bg-green-500 text-white p-5 rounded-lg shadow-2xl transition-all flex items-center justify-center hover:scale-110 active:scale-95 border-2 border-white/10">
        <MessageCircle className="size-8" />
      </div>
    </Link>;
}
function MobileMenuConsumer({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-xl md:hidden">
      <div className="flex h-20 items-center justify-between px-6 border-b border-border">
        <Link to="/" className="flex items-center" onClick={onClose}>
          <WvcLogo className="h-7 w-auto" />
        </Link>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fermer le menu">
          <X className="size-6 text-foreground" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto py-8 px-6">
        <nav className="flex flex-col space-y-6">
          {loading ? <div className="space-y-4">
              <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
              <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
              <div className="h-8 w-24 bg-muted animate-pulse rounded-md" />
            </div> : menuItems.map((item, i) => <div key={item.id} data-index={i}>
                <Link to={item.href || "#"} className="text-2xl font-serif font-medium text-foreground hover:text-primary transition-colors" onClick={onClose}>
                  {item.label}
                </Link>
              </div>)}
        </nav>
      </div>
    </div>;
}
export default function CorporateCoffeeCapsulesAndMachines() {
  const {
    pathname
  } = useLocation();
  const isHomePage = pathname === "/" || pathname === "/HomePage";
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: capsuleRef,
    isVisible: isCapsuleVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: false
  });
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="capsules-machines-café-pour-entreprises" className={`relative ${isHomePage ? "min-h-screen" : ""} flex flex-col bg-background`}>
      <header className={`sticky top-0 z-40 w-full transition-all duration-300 ${isScrolled ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm py-3" : "bg-background py-5"}`}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center z-50 relative">
            <WvcLogo className="h-7 w-auto" />
            <span className="ml-3 text-lg font-serif font-bold text-foreground">KBC Coffee Service</span>
          </Link>

          <div className="hidden md:flex items-center">
            <Button asChild className="!bg-green-600 hover:!bg-green-500 !text-white shadow-md hover:shadow-lg transition-all px-6 h-10 font-bold hover:scale-105 active:scale-95 border-none">
              <Link to="https://wa.me/221781666638?text=Bonjour%20KBC%20Coffee%20Service%2C%0A%0AJe%20souhaite%20des%20informations%20sur%20vos%20produits.%0A%0ALocalisation%20%3A%20(Thi%C3%A8s%20%2F%20Dakar%20%2F%20Petite%20C%C3%B4te)%0ABesoin%20%3A%20Capsules%20%2F%20Machine%20%2F%20Entreprise%0A%0AMerci." newTab>
                <MessageCircle className="size-4" />
                WhatsApp
              </Link>
            </Button>
          </div>

          <MenuProvider menu_id="102">
            <DesktopMenuConsumer />
          </MenuProvider>

          <Button variant="ghost" size="icon" className="md:hidden z-50 relative text-foreground" onClick={() => setIsMobileMenuOpen(true)} aria-label="Ouvrir le menu">
            <Menu className="size-6" />
          </Button>
        </div>
      </header>

      <WhatsAppFloatingButton />

      <MenuProvider menu_id="102">
        <MobileMenuConsumer isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>

      {isHomePage && <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 relative">
          <div className="bg-primary text-primary-foreground flex flex-col justify-center px-6 py-20 lg:px-16 xl:px-24 relative overflow-hidden">
            <div ref={textRef} className={`max-w-xl z-10 transition-all duration-1000 transform ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <span className="inline-block py-1 px-3 rounded-lg text-accent-foreground text-sm font-medium tracking-wider mb-6 uppercase !bg-[hsl(23,83%,31%)]">
                L'Art du Quotidien
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6 text-primary-foreground"> Capsules & Machines Premium livrées à Dakar & Thiès<br /></h1>
              <p className="text-lg md:text-xl text-primary-foreground/80 mb-10 max-w-md font-sans font-light leading-relaxed">Capsules compatibles Nespresso, Dolce Gusto &amp; Tassimo avec livraison rapide et commande simple via WhatsApp</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-accent/50 text-accent-foreground hover:bg-accent/70 text-base h-12 px-8">
                  <Link to="https://wa.me/221781666638?text=Bonjour%2C%20je%20suis%20interesse%20par%20vos%20capsules%20cafe.%20Pouvez-vous%20me%20renseigner%20sur%20les%20prix%20et%20la%20disponibilite%20%3F" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs rounded-lg has-[>svg]:px-4 bg-accent/50 text-base h-12 px-8 inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs rounded-lg has-[>svg]:px-4 text-base h-12 px-8 rounded-lg px-8 py-6 text-lg font-semibold shadow-lg transition-transform !bg-[hsl(142,71%,45%)] !text-[hsl(0,0%,100%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-accent/70 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-accent/70 hover:scale-105">Commander maintenant</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground hover:text-primary bg-transparent text-base h-12 px-8">
                  <Link to="/EnterpriseOfferPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border shadow-xs rounded-lg has-[>svg]:px-4 border-primary-foreground/30 text-primary-foreground text-base h-12 px-8 !bg-[hsl(0,94%,82%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-primary-foreground hover:text-primary">Découvrir nos offres Entreprise</Link>
                </Button>
              </div>
            </div>
          </div>

          <div ref={capsuleRef} className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 hidden md:block transition-all duration-1000 ease-out ${isCapsuleVisible ? "opacity-100 scale-100" : "opacity-0 scale-75"}`}>
            <div className="relative w-32 h-32 lg:w-48 lg:h-48 xl:w-56 xl:h-56">
              <div className="absolute inset-0 bg-accent/30 rounded-lg blur-3xl animate-pulse" />
            </div>
          </div>
        </div>}
    </section>;
}